import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  type IntakeFacts,
  type StandaloneSetInput,
  type StorageAdapter,
} from "@njbsoft/field-sync";
import { deviceCategory } from "@njbsoft/device-input/core";
import { formatHumanCode, uuidv7 } from "@njbsoft/utils";
import { InfoCircleIcon } from "@/components/icons";
import { PageHeader, PageSkeleton } from "@/components/ui";
import { DraftLockedElsewhere } from "@/components/draft-locked-elsewhere";
import { enqueueSet } from "@/components/tester/change-requests/staging/enqueue-set";
import { IntakeSaved } from "@/components/tester/change-requests/staging/intake-saved";
import { IntakeAssembliesStep } from "@/components/tester/change-requests/staging/intake-assemblies-step";
import { IntakeFacilityStep } from "@/components/tester/change-requests/staging/intake-facility-step";
import {
  type IntakeCandidatesResolution,
  type IntakeReads,
  type IntakeStep,
  type IntakeWriter,
} from "@/components/tester/change-requests/staging/intake-model";
import { IntakeReviewStep } from "@/components/tester/change-requests/staging/intake-review-step";
import { IntakeStepper } from "@/components/tester/change-requests/staging/intake-stepper";
import { IntakeUtilityStep } from "@/components/tester/change-requests/staging/intake-utility-step";
import { keptTests } from "@/components/tester/change-requests/staging/resubmit";
import { ResubmissionPin } from "@/components/tester/change-requests/staging/resubmission-pin";
import {
  type IntakeDraft,
  type IntakeDraftSidecar,
  type IntakeSidecar,
  type LockedIntakeSidecar,
  useIntakeDraft,
} from "@/components/tester/change-requests/staging/use-intake-draft";
import { useCheckIntakeCandidatesAtSend } from "@/hooks/use-check-intake-candidates-at-send";
import { useDraftLock } from "@/hooks/use-draft-lock";
import { useIsOnline } from "@/hooks/use-is-online";
import { usePersona } from "@/hooks/use-persona";
import { useFieldworkStorage } from "@/lib/fieldwork/storage-provider";
import { type Grant } from "@/lib/resolve-persona";

interface NewInstallationSearch {
  draft?: string;
  step?: IntakeStep | "saved";
}

const SEARCH_STEPS: readonly NonNullable<NewInstallationSearch["step"]>[] = [
  "utility",
  "facility",
  "assemblies",
  "review",
  "saved",
];

export const Route = createFileRoute("/_company/my-work/new-installation")({
  validateSearch: (search: Record<string, unknown>): NewInstallationSearch => ({
    draft: typeof search.draft === "string" && search.draft !== "" ? search.draft : undefined,
    step: SEARCH_STEPS.find((step) => step === search.step),
  }),
  component: NewInstallationPage,
});

function Skeleton() {
  return (
    <div className="p-4 sm:p-8">
      <PageSkeleton />
    </div>
  );
}

/**
 * A new installation: an assembly that is on no record, staged with its
 * facility entirely on this device. With no draft in the URL the page opens
 * one under a fresh client set id; a draft handed over locked to a facility
 * skips the facility entry. The saved state shows only in the session that
 * sent the set: a load at `step=saved`, or of a draft whose set is already
 * queued, goes to My Work, whose waiting card says where the set stands.
 */
function NewInstallationPage() {
  const { draft, step } = Route.useSearch();
  const navigate = useNavigate();
  const [minted] = useState(() => uuidv7());
  const saved = step === "saved";

  useEffect(() => {
    if (saved) void navigate({ to: "/my-work", search: { view: "cards" }, replace: true });
  }, [saved, navigate]);

  if (saved) return <Skeleton />;
  return <IntakeLocked clientSetId={draft ?? minted} opening={draft === undefined} />;
}

function IntakeLocked({ clientSetId, opening }: { clientSetId: string; opening: boolean }) {
  const draftLock = useDraftLock(clientSetId);
  if (draftLock === "heldElsewhere") return <DraftLockedElsewhere />;
  if (draftLock === "acquiring") return <Skeleton />;
  return <IntakeDraftPage clientSetId={clientSetId} opening={opening} />;
}

function openingDraft(grants: readonly Grant[]): IntakeSidecar {
  const sole = grants.length === 1 ? grants[0] : undefined;
  return {
    kind: "draft_set",
    anchor: "intake",
    orgId: sole?.orgId ?? null,
    step: sole === undefined ? "utility" : "facility",
    arrivedViaLetterCode: null,
    intake: {},
    items: [],
    resubmission: null,
  };
}

/** The set the draft queues: its org, its anchor and its body members, with the at-send question's resolution when it was asked. */
type SetSend = Pick<
  StandaloneSetInput,
  "orgId" | "anchor" | "facilityId" | "intake" | "items" | "arrivedViaLetterCode"
> & { resolution?: IntakeCandidatesResolution };

/** What the later steps run on: the store, the utility's reads, and the send. */
interface Staging {
  storage: StorageAdapter;
  reads: IntakeReads;
  send: (set: SetSend) => Promise<void>;
}

function IntakeDraftPage({ clientSetId, opening }: { clientSetId: string; opening: boolean }) {
  const { t } = useTranslation("change-requests");
  const navigate = useNavigate();
  const { storage, engine } = useFieldworkStorage();
  const { resolution } = usePersona();
  // Offline from a cold start the admitted arm is the persisted snapshot's; a
  // suspended or revoked authorization never serves a new installation.
  const grants =
    resolution.kind === "admitted"
      ? resolution.grants.filter((grant) => grant.status === "active")
      : [];
  const draft = useIntakeDraft(clientSetId, opening ? openingDraft(grants) : null);
  const orgId = draft.sidecar?.orgId ?? null;
  const lockedFacilityId =
    draft.sidecar?.anchor === "standalone" ? draft.sidecar.facility.id : null;
  const resubmitting = (draft.sidecar?.resubmission ?? null) !== null;
  const [reads, setReads] = useState<(IntakeReads & { orgId: string }) | null>(null);
  // Whether this load found the draft's set already queued (then it leaves for
  // My Work), and whether this session sent it (then the saved state shows).
  const [queued, setQueued] = useState<boolean | null>(null);
  const [sent, setSent] = useState(false);
  // A load-time read that fails leaves the page with nothing to render from.
  const [readFailed, setReadFailed] = useState(false);

  useEffect(() => {
    if (storage === null) return;
    let stale = false;
    storage
      .listChangeRequestSetRows()
      .then((rows) => {
        if (stale) return;
        const found = rows.some((row) => row.clientSetId === clientSetId);
        setQueued(found);
        if (found) void navigate({ to: "/my-work", search: { view: "cards" }, replace: true });
      })
      .catch((err: unknown) => {
        // A read the unmount outran settles against a store this page no longer
        // reads from; only a mounted page has a failure to report.
        if (stale) return;
        console.error("intake set-row read failed", err);
        setReadFailed(true);
      });
    return () => {
      stale = true;
    };
  }, [storage, clientSetId, navigate]);

  useEffect(() => {
    if (storage === null || orgId === null) return;
    let stale = false;
    void (async () => {
      const [template, requirements, kits, bundleFacility, sets] = await Promise.all([
        storage.getCategoryTemplate(orgId, "testable_assembly"),
        storage.getUtilityRequirements(orgId),
        storage.listBundleKits(),
        lockedFacilityId === null ? null : storage.getBundleFacility(lockedFacilityId),
        resubmitting ? storage.listBundleChangeRequestSets() : [],
      ]);
      if (stale) return;
      setReads({ orgId, template, requirements, kits, bundleFacility, kept: keptTests(sets) });
    })().catch((err: unknown) => {
      if (stale) return;
      console.error("intake draft reads failed", err);
      setReadFailed(true);
    });
    return () => {
      stale = true;
    };
  }, [storage, orgId, lockedFacilityId, resubmitting]);

  if (draft.status === "error" || readFailed) {
    return (
      <p role="alert" className="p-4 text-sm text-danger sm:p-8">
        {t("intake.loadFailed")}
      </p>
    );
  }
  if (draft.sidecar === null || queued !== false) return <Skeleton />;
  const { sidecar } = draft;
  if (sent) {
    return (
      <IntakeSaved
        clientSetId={clientSetId}
        facilityName={
          sidecar.anchor === "standalone"
            ? sidecar.facility.name
            : (sidecar.intake.facility_name ?? "")
        }
        utilityName={
          sidecar.anchor === "standalone"
            ? sidecar.facility.orgName
            : (grants.find((grant) => grant.orgId === sidecar.orgId)?.orgName ?? "")
        }
        assemblies={sidecar.items.length}
        tests={
          sidecar.items.filter((entry) => deviceCategory(entry.deviceInput) === "testable_assembly")
            .length
        }
      />
    );
  }
  const staging: Staging | null =
    storage === null || engine === null || reads === null || reads.orgId !== orgId
      ? null
      : {
          storage,
          reads,
          // The sidecar stays after the send: the received set's cleanup finds
          // the staged tests' photo owners through it, then deletes it.
          send: async (set) => {
            const { resolution, ...input } = set;
            await enqueueSet(storage, engine, {
              ...input,
              // A pick converts the set to the facility the tester chose —
              // the same body the waiting card's answer writes.
              ...(resolution?.kind === "pick"
                ? { anchor: "standalone" as const, facilityId: resolution.facilityId }
                : {}),
              ...(resolution?.kind === "none"
                ? {
                    dismissedCandidates: {
                      candidates: resolution.candidates,
                      ...(resolution.reason !== undefined ? { reason: resolution.reason } : {}),
                    },
                  }
                : {}),
              now: new Date(),
              clientSetId,
            });
            setSent(true);
          },
        };
  if (sidecar.anchor === "standalone") {
    return (
      <LockedIntake
        clientSetId={clientSetId}
        sidecar={sidecar}
        update={draft.update}
        staging={staging}
      />
    );
  }
  return (
    <UnlockedIntake
      clientSetId={clientSetId}
      sidecar={sidecar}
      grants={grants}
      update={draft.update}
      staging={staging}
    />
  );
}

function OfflineBanner() {
  const { t } = useTranslation("change-requests");
  const online = useIsOnline();
  if (online) return null;
  return (
    <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      {t("intake.offline")}
    </p>
  );
}

/** The facts as the wire takes them: every required one present, as the Facility step saves them. */
function sendableFacts(facts: Partial<IntakeFacts>): IntakeFacts | null {
  const { facility_name, address_line1, city, state_code, zip_code, owner_name, owner_phone } =
    facts;
  if (
    facility_name === undefined ||
    address_line1 === undefined ||
    city === undefined ||
    state_code === undefined ||
    zip_code === undefined ||
    owner_name === undefined ||
    owner_phone === undefined
  ) {
    return null;
  }
  return {
    ...facts,
    facility_name,
    address_line1,
    city,
    state_code,
    zip_code,
    owner_name,
    owner_phone,
  };
}

function useGoTo(clientSetId: string, update: IntakeDraft["update"]) {
  const navigate = useNavigate();
  const goTo = (next: IntakeStep) => {
    update((current: IntakeDraftSidecar) => ({ ...current, step: next })).catch(() => undefined);
    void navigate({ to: "/my-work/new-installation", search: { draft: clientSetId, step: next } });
  };
  const toMyWork = () => {
    void navigate({ to: "/my-work", search: { view: "cards" } });
  };
  return { goTo, toMyWork };
}

function UnlockedIntake({
  clientSetId,
  sidecar,
  grants,
  update,
  staging,
}: {
  clientSetId: string;
  sidecar: IntakeSidecar;
  grants: readonly Grant[];
  update: IntakeDraft["update"];
  staging: Staging | null;
}) {
  const { t } = useTranslation("change-requests");
  const { step: searchStep } = Route.useSearch();
  const { goTo, toMyWork } = useGoTo(clientSetId, update);
  // The at-send candidate check is the hooks-layer online wrapper — it hands
  // back null while offline (the press queues straight; the drain asks), so
  // the step's check applies only while one can run.
  const checkAtSend = useCheckIntakeCandidatesAtSend();
  // The utility the draft sends to, only while it is still an active authorization.
  const org = grants.find((grant) => grant.orgId === sidecar.orgId) ?? null;
  const first: IntakeStep = grants.length === 1 && org !== null ? "facility" : "utility";
  const steps: readonly IntakeStep[] =
    first === "facility"
      ? ["facility", "assemblies", "review"]
      : ["utility", "facility", "assemblies", "review"];
  const facts = sendableFacts(sidecar.intake);
  const wanted = searchStep ?? sidecar.step;
  const offered = steps.find((key) => key === wanted) ?? first;
  // The assemblies build on an active utility and the saved facts; a step
  // reached without them opens where they are asked.
  const later = offered === "assemblies" || offered === "review";
  const step: IntakeStep =
    later && org === null ? "utility" : later && facts === null ? "facility" : offered;

  const write: IntakeWriter = useCallback(
    (change) =>
      update((current) => {
        if (current.anchor !== "intake") throw new Error("intake_draft_missing");
        return change(current);
      }),
    [update],
  );

  let body;
  if (step === "utility") {
    body = (
      <IntakeUtilityStep
        grants={grants}
        orgId={sidecar.orgId}
        write={write}
        onContinue={() => goTo("facility")}
        onCancel={toMyWork}
      />
    );
  } else if (step === "facility") {
    body = (
      <IntakeFacilityStep
        clientSetId={clientSetId}
        facts={sidecar.intake}
        write={write}
        back={t("common:back")}
        onBack={first === "utility" ? () => goTo("utility") : toMyWork}
        onContinue={() => goTo("assemblies")}
      />
    );
  } else if (staging === null || org === null || facts === null) {
    body = <PageSkeleton />;
  } else {
    const props = {
      storage: staging.storage,
      reads: staging.reads,
      clientSetId,
      sidecar,
      update,
      orgId: org.orgId,
      utilityName: org.orgName,
    };
    body =
      step === "assemblies" ? (
        <IntakeAssembliesStep
          {...props}
          onBack={() => goTo("facility")}
          onContinue={() => goTo("review")}
        />
      ) : (
        <IntakeReviewStep
          {...props}
          typed={{ facts, onEdit: () => goTo("facility") }}
          onBack={() => goTo("assemblies")}
          onSaveLater={toMyWork}
          checkCandidates={checkAtSend === null ? undefined : () => checkAtSend(org.orgId, facts)}
          send={(items, resolution) =>
            staging.send({ orgId: org.orgId, anchor: "intake", intake: facts, items, resolution })
          }
        />
      );
  }

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader
        title={t("intake.title")}
        subtitle={
          step === "utility"
            ? t("intake.intro")
            : step === "assemblies" && facts !== null && org !== null
              ? t("intake.facilityLine", {
                  facility: facts.facility_name,
                  street: facts.address_line1,
                  city: facts.city,
                  state: facts.state_code,
                  zip: facts.zip_code,
                  utility: org.orgName,
                })
              : undefined
        }
        backTo="/my-work"
        backLabel={t("intake.backToMyWork")}
      />
      {sidecar.resubmission !== null && <ResubmissionPin pin={sidecar.resubmission} />}
      <IntakeStepper steps={steps} step={step} />
      <OfflineBanner />
      {/* The review's facility card says it with the utility's name. */}
      {step !== "review" && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-on-surface">
          <InfoCircleIcon className="mt-0.5 h-4 w-4 flex-none text-primary" aria-hidden="true" />
          <p>
            <b>{t("intake.checkAtSend.lead")}</b> {t("intake.checkAtSend.rest")}
          </p>
        </div>
      )}
      {body}
    </div>
  );
}

const LOCKED_STEPS: readonly IntakeStep[] = ["assemblies", "review"];

function LockedIntake({
  clientSetId,
  sidecar,
  update,
  staging,
}: {
  clientSetId: string;
  sidecar: LockedIntakeSidecar;
  update: IntakeDraft["update"];
  staging: Staging | null;
}) {
  const { t } = useTranslation("change-requests");
  const navigate = useNavigate();
  const { step: searchStep } = Route.useSearch();
  const { goTo, toMyWork } = useGoTo(clientSetId, update);
  const { facility, arrivedViaLetterCode, orgId } = sidecar;
  const wanted = searchStep ?? sidecar.step;
  const step = LOCKED_STEPS.find((key) => key === wanted) ?? "assemblies";

  let body;
  if (staging === null) {
    body = <PageSkeleton />;
  } else {
    const props = {
      storage: staging.storage,
      reads: staging.reads,
      clientSetId,
      sidecar,
      update,
      orgId,
      utilityName: facility.orgName,
    };
    body =
      step === "assemblies" ? (
        <IntakeAssembliesStep
          {...props}
          onBack={() => {
            void navigate({
              to: "/claim/facility/$facilityId",
              params: { facilityId: facility.id },
            });
          }}
          onContinue={() => goTo("review")}
        />
      ) : (
        <IntakeReviewStep
          {...props}
          typed={null}
          onBack={() => goTo("assemblies")}
          onSaveLater={toMyWork}
          send={(items) =>
            staging.send({
              orgId,
              anchor: "standalone",
              facilityId: facility.id,
              items,
              arrivedViaLetterCode: arrivedViaLetterCode ?? undefined,
            })
          }
        />
      );
  }

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader
        title={t("intake.lockedTitle")}
        backTo={`/claim/facility/${facility.id}`}
        backLabel={t("intake.backToFacility")}
      />
      {sidecar.resubmission !== null && <ResubmissionPin pin={sidecar.resubmission} />}
      <IntakeStepper steps={LOCKED_STEPS} step={step} />
      <OfflineBanner />
      <div className="space-y-3">
        <p className="rounded-lg bg-gray-50 px-4 py-3 text-sm dark:bg-gray-800">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">
            {t("intake.locked.facility")}
          </span>{" "}
          <span className="font-medium text-on-surface">
            {[facility.name, facility.addressLine].filter(Boolean).join(" — ")}
          </span>
          {arrivedViaLetterCode === null && (
            <>
              {" "}
              <span className="text-xs text-muted">
                {t(
                  sidecar.resubmission === null
                    ? "intake.locked.fromFacility"
                    : "resubmit.nowOnRecord",
                )}
              </span>
            </>
          )}
        </p>
        {arrivedViaLetterCode !== null && (
          <p className="text-sm text-on-surface">
            {t("intake.locked.arrivedVia")}{" "}
            <span className="font-mono">{formatHumanCode(arrivedViaLetterCode)}</span>
          </p>
        )}
      </div>
      {body}
    </div>
  );
}
